import { useEffect, useState } from 'react'
import { AccountLink } from '../components/Drill'
import { listMeetings, type RemoteMeeting } from '../lib/adminClient'

// Live meetings are ephemeral. They exist only while people are in a room, so an
// empty list is the normal resting state, not a failure. We render the server's
// `note` as context so nobody reads "no rooms" as "the feature is broken".

export default function Meetings(): JSX.Element {
  const [meetings, setMeetings] = useState<RemoteMeeting[] | null>(null)
  const [note, setNote] = useState<string>('')
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading')
  const [error, setError] = useState<string | null>(null)

  async function load(): Promise<void> {
    setState('loading')
    const res = await listMeetings()
    if (res.ok) {
      setMeetings(res.data.meetings)
      setNote(res.data.note ?? '')
      setState('ready')
    } else {
      setError(res.error)
      setState('error')
    }
  }

  useEffect(() => {
    void load()
  }, [])

  const totalParticipants = (meetings ?? []).reduce((n, m) => n + m.participantCount, 0)

  const header = (
    <div className="mb-6 flex items-end justify-between gap-4">
      <div>
        <div className="text-[11px] uppercase tracking-[0.3em] text-slate-400">Live</div>
        <h1 className="text-2xl font-semibold mt-1">Meetings</h1>
        <div className="text-sm text-slate-400 mt-1">
          Rooms with people in them right now. Meetings are live only and leave no record once everyone has left.
        </div>
      </div>
      <button className="btn-ghost" onClick={() => void load()} disabled={state === 'loading'}>
        Refresh
      </button>
    </div>
  )

  if (state !== 'ready') {
    return (
      <div>
        {header}
        <div className="glass-card rounded-xl p-6 text-sm text-slate-400">
          {state === 'loading' ? 'Loading meetings…' : `Could not load meetings: ${error}`}
        </div>
      </div>
    )
  }

  const list = meetings ?? []

  return (
    <div>
      {header}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <div className="glass-card rounded-xl p-4 border border-white/5">
          <div className="text-[10px] uppercase tracking-[0.16em] text-slate-400">Active rooms</div>
          <div className="text-xl font-semibold mt-1">{list.length}</div>
        </div>
        <div className="glass-card rounded-xl p-4 border border-white/5">
          <div className="text-[10px] uppercase tracking-[0.16em] text-slate-400">People in a meeting</div>
          <div className="text-xl font-semibold mt-1">{totalParticipants}</div>
        </div>
      </div>

      {list.length === 0 ? (
        <div className="glass-card rounded-xl p-6 text-sm text-slate-400">
          No live meetings right now.
          {note ? <div className="text-[13px] text-slate-500 mt-2">{note}</div> : null}
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {list.map((m) => (
              <div key={m.roomId} className="glass-card rounded-xl p-5">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.16em] text-slate-400">Room</div>
                    <div className="text-sm font-medium mt-0.5 font-mono text-slate-200">{m.roomId}</div>
                  </div>
                  <span className="px-2.5 py-1 rounded-full text-xs bg-emerald-500/15 text-emerald-300">
                    {m.participantCount} {m.participantCount === 1 ? 'participant' : 'participants'}
                  </span>
                </div>
                {m.participants.length === 0 ? (
                  <div className="text-xs text-slate-500">No participant details reported.</div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {m.participants.map((p) => (
                      <span
                        key={p.accountId}
                        className="px-2 py-1 rounded-full text-xs bg-white/5 text-slate-200"
                        title={p.accountId}
                      >
                        {/* A live room is held in memory; its participants are the records. */}
                        <AccountLink id={p.accountId}>{p.displayName || (p.handle ? `@${p.handle}` : p.accountId)}</AccountLink>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
          {note ? <div className="text-[11px] text-slate-500 mt-4">{note}</div> : null}
        </>
      )}
    </div>
  )
}
